import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import ValuesetPicker from '@/components/ValuesetPicker';
import { ArrowLeft, Save, Database, Code, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function ApplicabilityPage() {
  const { attributeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get(`/attributes/${attributeId}/applicability`);
      setData(r.data);
      setRows(r.data.schemas.map(s => ({ ...s })));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [attributeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateRow = (schemaId, field, value) => {
    setRows(prev => prev.map(r => r.schema_id === schemaId ? { ...r, [field]: value } : r));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const mappings = rows.map(r => ({
        schema_id: r.schema_id,
        enabled: r.enabled,
        valueset_id: r.valueset_id || null,
        is_required: r.is_required,
      }));
      const res = await api.put(`/attributes/${attributeId}/applicability`, { mappings });
      toast.success(res.data.message);
      setDirty(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white"><Navbar />
        <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white"><Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12 text-center text-[#9CA3AF]">Attribute not found</div>
      </div>
    );
  }

  const attr = data.attribute;
  const valuesets = data.valuesets || [];
  const enabledCount = rows.filter(r => r.enabled).length;

  return (
    <div className="min-h-screen bg-white" data-testid="applicability-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/attributes/${attributeId}`)} className="h-7 px-2 rounded-none text-[#4B5563]" data-testid="back-to-attr">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> {attr.name}
          </Button>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-sm font-medium text-[#0A0A0A]">Schema Applicability</span>
        </div>

        {/* Header */}
        <div className="border-b border-[#E5E7EB] pb-5 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Schema Applicability
              </h1>
              <p className="text-sm text-[#4B5563]">
                Manage which schemas include <strong>{attr.name}</strong> and with which valueset
              </p>
              <p className="text-xs text-[#9CA3AF] mt-1">{enabledCount} of {rows.length} schemas enabled</p>
            </div>
            {dirty && (
              <Button onClick={handleSave} disabled={saving} className="rounded-none bg-[#002FA7] hover:bg-[#002585] text-xs h-8" data-testid="save-applicability-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </div>
        </div>

        {/* Applicability Grid */}
        <div className="border border-[#E5E7EB]" data-testid="applicability-grid">
          {/* Header */}
          <div className="grid grid-cols-12 gap-0 bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB]">
            <div className="col-span-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">On</div>
            <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Schema</div>
            <div className="col-span-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Valueset</div>
            <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Required</div>
            <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Status</div>
          </div>

          {rows.map(row => (
            <div
              key={row.schema_id}
              className={`grid grid-cols-12 gap-0 items-center px-4 py-3 border-b border-[#E5E7EB] last:border-b-0 transition-colors ${
                row.enabled ? 'bg-white' : 'bg-zinc-50/50'
              }`}
              data-testid={`applicability-row-${row.schema_code}`}
            >
              <div className="col-span-1">
                <Switch
                  checked={row.enabled}
                  onCheckedChange={v => updateRow(row.schema_id, 'enabled', v)}
                  data-testid={`toggle-${row.schema_code}`}
                />
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-[#002FA7]" strokeWidth={1.5} />
                <div>
                  <span className="text-sm font-medium text-[#0A0A0A]">{row.schema_name}</span>
                  <span className="text-[10px] text-[#9CA3AF] ml-1.5 uppercase">{row.schema_code}</span>
                </div>
              </div>
              <div className="col-span-4">
                {row.enabled ? (
                  <ValuesetPicker
                    value={row.valueset_id}
                    onChange={v => updateRow(row.schema_id, 'valueset_id', v)}
                    valuesets={valuesets}
                    triggerClassName="h-8 text-xs w-full"
                    testId={`vs-select-${row.schema_code}`}
                  />
                ) : (
                  <span className="text-xs text-[#9CA3AF]">—</span>
                )}
              </div>
              <div className="col-span-2">
                {row.enabled ? (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={row.is_required}
                      onCheckedChange={v => updateRow(row.schema_id, 'is_required', !!v)}
                      className="h-4 w-4 rounded-none"
                      data-testid={`required-${row.schema_code}`}
                    />
                    <span className="text-xs text-[#4B5563]">Required</span>
                  </div>
                ) : (
                  <span className="text-xs text-[#9CA3AF]">—</span>
                )}
              </div>
              <div className="col-span-2">
                {row.enabled ? (
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 font-semibold flex items-center gap-1 w-fit">
                    <Check className="w-3 h-3" /> Active
                  </span>
                ) : (
                  <span className="text-[10px] text-[#9CA3AF]">Inactive</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {dirty && <p className="text-[10px] text-amber-600 mt-2 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Unsaved changes</p>}
      </div>
    </div>
  );
}
